import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, Grid, Chip, Alert,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Divider,
} from "@mui/material";
import axios, { getActiveWorkspace } from "../axiosConfig";

const fmtPct = (x) => (x == null ? "—" : `${(x * 100).toFixed(1)}%`);
const fmtUsd = (x) => (x == null ? "—" : `$${Number(x).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

export default function EnsembleTradingPanel() {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [candidates, setCandidates] = useState(null);
  const [rebalance, setRebalance] = useState(null);
  const [exits, setExits] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [attr, setAttr] = useState(null);
  const [ws, setWs] = useState(null);
  const [keyRef, setKeyRef] = useState("");

  const slug = getActiveWorkspace();

  const loadWorkspace = useCallback(async () => {
    try {
      const r = await axios.get(`/api/workspaces/${slug}`);
      if (r.data?.success) { setWs(r.data.data); setKeyRef(r.data.data.alpaca_key_ref || ""); }
    } catch (e) { /* default workspace etc. */ }
  }, [slug]);

  const loadPerf = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([axios.get("/api/trading/pnl"), axios.get("/api/trading/attribution")]);
      if (p.data?.success) setPnl(p.data.data);
      if (a.data?.success) setAttr(a.data.data);
    } catch (e) { /* not configured */ }
  }, []);

  useEffect(() => { loadWorkspace(); loadPerf(); }, [loadWorkspace, loadPerf]);

  const run = async (key, fn) => {
    setBusy(key); setErr("");
    try { await fn(); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(""); }
  };

  const previewCandidates = () => run("cand", async () => {
    const r = await axios.get("/api/trading/candidates");
    setCandidates(r.data.data || []);
  });
  const buildRebalance = () => run("reb", async () => {
    const r = await axios.post("/api/trading/rebalance", {});
    setRebalance(r.data.data);
  });
  const checkExits = () => run("exit", async () => {
    const r = await axios.post("/api/trading/run-exit-monitor", { dry_run: true });
    setExits(r.data.data);
  });
  const snapshot = () => run("snap", async () => {
    await axios.post("/api/trading/snapshot", {});
    await loadPerf();
  });
  const saveKeyRef = () => run("key", async () => {
    if (!ws?.id) throw new Error("workspace not loaded");
    await axios.put(`/api/workspaces/${ws.id}`, { alpaca_key_ref: keyRef });
    await loadWorkspace();
  });

  const Spin = ({ k }) => (busy === k ? <CircularProgress size={16} sx={{ ml: 1 }} /> : null);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Ensemble Strategy — <Chip label={ws?.name || slug} size="small" color="primary" />
      </Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr("")}>{err}</Alert>}

      <Grid container spacing={2}>
        {/* Performance */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" gutterBottom>Performance</Typography>
            {pnl?.metrics ? (
              <Grid container spacing={1}>
                <Stat label="Total return" v={fmtPct(pnl.metrics.total_return)} />
                <Stat label="Sharpe" v={pnl.metrics.sharpe ?? "—"} />
                <Stat label="Max DD" v={fmtPct(pnl.metrics.max_drawdown)} />
                <Stat label="Equity" v={fmtUsd(pnl.metrics.end_equity)} />
                <Stat label="Days tracked" v={pnl.metrics.days} />
              </Grid>
            ) : <Typography variant="body2" color="text.secondary">No snapshots yet.</Typography>}
            <Button size="small" sx={{ mt: 1 }} disabled={!!busy} onClick={snapshot}>
              Snapshot now<Spin k="snap" />
            </Button>
          </CardContent></Card>
        </Grid>

        {/* Attribution */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" gutterBottom>P&L attribution (open book)</Typography>
            {attr?.by_agent && Object.keys(attr.by_agent).length ? (
              <Table size="small"><TableBody>
                {Object.entries(attr.by_agent).map(([a, v]) => (
                  <TableRow key={a}><TableCell>{a}</TableCell>
                    <TableCell align="right" sx={{ color: v >= 0 ? "success.main" : "error.main" }}>
                      {fmtUsd(v)}</TableCell></TableRow>
                ))}
                <TableRow><TableCell><b>Total unrealized</b></TableCell>
                  <TableCell align="right"><b>{fmtUsd(attr.total_unrealized)}</b></TableCell></TableRow>
              </TableBody></Table>
            ) : <Typography variant="body2" color="text.secondary">No attributable positions.</Typography>}
          </CardContent></Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle2" gutterBottom>Cycle</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Button variant="outlined" size="small" disabled={!!busy} onClick={previewCandidates}>
                Preview candidates<Spin k="cand" /></Button>
              <Button variant="outlined" size="small" disabled={!!busy} onClick={buildRebalance}>
                Build rebalance<Spin k="reb" /></Button>
              <Button variant="outlined" size="small" disabled={!!busy} onClick={checkExits}>
                Check exits<Spin k="exit" /></Button>
            </Box>

            {candidates && (
              <Box sx={{ mb: 1 }}><Typography variant="caption">Candidates ({candidates.length}):</Typography>{" "}
                {candidates.slice(0, 30).map((c) => (
                  <Chip key={c.ticker} label={c.ticker} size="small" title={c.reason} sx={{ mr: 0.5, mb: 0.5 }} />
                ))}</Box>
            )}

            {rebalance && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Rebalance built: {rebalance.proposals?.length || 0} proposals, gross {fmtPct(rebalance.gross)} ·
                run #{rebalance.run?.id}. Approve &amp; execute it in the Proposals section below.
              </Alert>
            )}

            {exits && (
              exits.actions?.length ? (
                <Table size="small"><TableHead><TableRow>
                  <TableCell>Ticker</TableCell><TableCell>Action</TableCell><TableCell>Qty</TableCell><TableCell>Reason</TableCell>
                </TableRow></TableHead><TableBody>
                  {exits.actions.map((a, i) => (
                    <TableRow key={i}><TableCell>{a.ticker}</TableCell><TableCell>{a.action}</TableCell>
                      <TableCell>{a.qty}</TableCell><TableCell>{a.reason}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
              ) : <Typography variant="body2" color="text.secondary">
                No exits triggered ({exits.holdings_checked} holdings checked).</Typography>
            )}
          </CardContent></Card>
        </Grid>

        {/* Workspace account / key (only meaningful for non-default workspaces) */}
        {ws && ws.id !== 1 && (
          <Grid item xs={12}>
            <Card variant="outlined"><CardContent>
              <Typography variant="subtitle2" gutterBottom>Paper account</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Set this workspace's Alpaca key reference. Secrets live in env as
                <code> ALPACA_KEY_&lt;REF&gt;</code> / <code>ALPACA_SECRET_&lt;REF&gt;</code>.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField size="small" label="alpaca_key_ref" value={keyRef}
                  onChange={(e) => setKeyRef(e.target.value)} sx={{ width: 240 }} />
                <Button size="small" variant="contained" disabled={!!busy} onClick={saveKeyRef}>
                  Save<Spin k="key" /></Button>
              </Box>
            </CardContent></Card>
          </Grid>
        )}
      </Grid>
      <Divider sx={{ mt: 3 }} />
    </Box>
  );
}

function Stat({ label, v }) {
  return (
    <Grid item xs={6} sm={4}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1">{v}</Typography>
    </Grid>
  );
}
